import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Database, DownloadSimple, UploadSimple, Warning, FileArchive, FileText } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

/**
 * BackupRestorePanel — Import / Export completo de la base de datos.
 * Solo visible para admin.
 *  - Exporta JSON o ZIP (todas las colecciones).
 *  - Importa JSON o ZIP con dos modos:
 *      · Merge: actualiza por `id` o inserta.
 *      · Replace: vacía cada colección antes de insertar (⚠️ destructivo).
 */
export default function BackupRestorePanel() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [confirmMode, setConfirmMode] = useState(null); // 'merge' | 'replace' | null
  const fileRef = useRef(null);

  const loadStats = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/db/stats");
      setStats(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  const doExport = async (format) => {
    setExporting(true);
    try {
      const resp = await api.get(`/admin/db/export?format=${format}`, {
        responseType: "blob",
      });
      const ts = new Date().toISOString().replace(/[-:T.]/g, "").slice(0, 14);
      const ext = format === "zip" ? "zip" : "json";
      const blob = new Blob([resp.data], {
        type: format === "zip" ? "application/zip" : "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mining_backup_${ts}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const totalDocs = resp.headers?.["x-backup-meta-total"];
      const totalCol = resp.headers?.["x-backup-meta-collections"];
      toast.success(
        `Respaldo descargado · ${totalCol || "?"} colecciones · ${totalDocs || "?"} documentos`
      );
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setExporting(false);
    }
  };

  const onFilePicked = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPendingFile(file);
    setImportResult(null);
  };

  const runImport = async () => {
    if (!pendingFile || !confirmMode) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", pendingFile);
      fd.append("mode", confirmMode);
      const { data } = await api.post("/admin/db/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      toast.success(
        `Restauración completada · ${data.collections_processed} colecciones`
      );
      setPendingFile(null);
      setConfirmMode(null);
      loadStats();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div
      className="bg-white border border-slate-200 rounded-lg p-8 max-w-3xl"
      data-testid="backup-restore-panel"
    >
      <div className="flex items-center gap-2 mb-1">
        <Database size={20} weight="duotone" className="text-blue-900" />
        <Label className="overline-label !m-0">
          Respaldo · Importar / Exportar Base de Datos
        </Label>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Descarga un respaldo completo de todas las colecciones de MongoDB o restaura
        el sistema desde un archivo anterior. Se recomienda descargar un respaldo
        antes de cualquier restauración destructiva.
      </p>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-5" data-testid="db-stats">
        <StatCard
          label="Base de datos"
          value={stats?.db_name || "…"}
          hint={loading ? "Cargando…" : "MongoDB"}
        />
        <StatCard
          label="Colecciones"
          value={stats?.collections ?? "…"}
          hint="totales"
        />
        <StatCard
          label="Documentos"
          value={
            typeof stats?.total_documents === "number"
              ? stats.total_documents.toLocaleString()
              : "…"
          }
          hint="acumulados"
        />
      </div>

      {/* Export */}
      <div className="border border-slate-200 rounded-md p-4 mb-4">
        <div className="flex items-center gap-2 mb-2">
          <DownloadSimple size={16} weight="bold" className="text-emerald-700" />
          <span className="font-bold text-sm text-slate-900">
            Exportar todo el sistema
          </span>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Genera un archivo con todas las colecciones (usuarios, HR, finanzas,
          HSE, operaciones, mantenimiento, ajustes, catálogos…). Guarda el
          archivo en un lugar seguro — contiene datos sensibles.
        </p>
        <div className="flex gap-2 flex-wrap">
          <Button
            data-testid="backup-export-json-btn"
            onClick={() => doExport("json")}
            disabled={exporting}
            className="bg-blue-900 hover:bg-blue-800 gap-2"
          >
            <FileText size={14} weight="bold" />
            {exporting ? "Generando…" : "Exportar JSON"}
          </Button>
          <Button
            data-testid="backup-export-zip-btn"
            onClick={() => doExport("zip")}
            disabled={exporting}
            variant="outline"
            className="gap-2"
          >
            <FileArchive size={14} weight="bold" />
            {exporting ? "Generando…" : "Exportar ZIP"}
          </Button>
        </div>
      </div>

      {/* Import */}
      <div className="border border-orange-200 bg-orange-50/50 rounded-md p-4">
        <div className="flex items-center gap-2 mb-2">
          <UploadSimple size={16} weight="bold" className="text-orange-700" />
          <span className="font-bold text-sm text-slate-900">
            Importar / Restaurar respaldo
          </span>
        </div>
        <p className="text-xs text-slate-600 mb-3">
          Sube un archivo previamente exportado (<code>.json</code> o{" "}
          <code>.zip</code>). Elige el modo con cuidado — <b>Reemplazar</b>{" "}
          vaciará las colecciones existentes antes de insertar.
        </p>

        <div className="flex items-center gap-3 flex-wrap mb-3">
          <Button
            onClick={() => fileRef.current?.click()}
            variant="outline"
            data-testid="backup-pick-file-btn"
            className="gap-2"
            disabled={importing}
          >
            <UploadSimple size={14} weight="bold" />
            {pendingFile ? "Cambiar archivo…" : "Seleccionar archivo…"}
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".json,.zip,application/json,application/zip"
            className="hidden"
            data-testid="backup-file-input"
            onChange={onFilePicked}
          />
          {pendingFile && (
            <span
              data-testid="backup-selected-file"
              className="text-xs font-mono bg-white border border-slate-200 rounded px-2 py-1"
            >
              {pendingFile.name} ·{" "}
              {(pendingFile.size / 1024).toFixed(1)} KB
            </span>
          )}
        </div>

        {pendingFile && !confirmMode && (
          <div className="grid sm:grid-cols-2 gap-3">
            <button
              type="button"
              data-testid="backup-mode-merge-btn"
              onClick={() => setConfirmMode("merge")}
              className="text-left bg-white border border-slate-200 rounded-md p-3 hover:border-blue-500 transition"
            >
              <div className="font-bold text-sm text-blue-900">🔀 Combinar (merge)</div>
              <p className="text-xs text-slate-500 mt-1">
                Actualiza documentos existentes por su <code>id</code> e inserta
                los nuevos. No borra información previa.
              </p>
            </button>
            <button
              type="button"
              data-testid="backup-mode-replace-btn"
              onClick={() => setConfirmMode("replace")}
              className="text-left bg-white border border-red-200 rounded-md p-3 hover:border-red-500 transition"
            >
              <div className="font-bold text-sm text-red-700">⚠️ Reemplazar todo</div>
              <p className="text-xs text-slate-500 mt-1">
                Vacía cada colección antes de insertar. <b>Perderás</b> los datos
                que no vengan en el respaldo.
              </p>
            </button>
          </div>
        )}

        {pendingFile && confirmMode && (
          <div
            className="bg-white border border-red-300 rounded-md p-3 mt-2"
            data-testid="backup-confirm-box"
          >
            <div className="flex items-start gap-2">
              <Warning
                size={20}
                weight="fill"
                className={
                  confirmMode === "replace"
                    ? "text-red-600 mt-0.5"
                    : "text-blue-900 mt-0.5"
                }
              />
              <div className="flex-1">
                <p className="text-sm font-bold text-slate-900">
                  {confirmMode === "replace"
                    ? "Confirmar reemplazo total"
                    : "Confirmar combinación"}
                </p>
                <p className="text-xs text-slate-600 mt-1">
                  {confirmMode === "replace" ? (
                    <>
                      Se <b>eliminarán</b> todos los documentos actuales de cada
                      colección incluida en el respaldo antes de insertar los
                      nuevos. Esta acción no se puede deshacer.
                    </>
                  ) : (
                    <>
                      Se actualizarán o insertarán documentos por su{" "}
                      <code>id</code>. Los datos actuales que no colisionen se
                      conservan.
                    </>
                  )}
                </p>
                <div className="flex gap-2 mt-3">
                  <Button
                    onClick={runImport}
                    disabled={importing}
                    data-testid="backup-confirm-run-btn"
                    className={
                      confirmMode === "replace"
                        ? "bg-red-600 hover:bg-red-700"
                        : "bg-blue-900 hover:bg-blue-800"
                    }
                  >
                    {importing
                      ? "Restaurando…"
                      : confirmMode === "replace"
                      ? "Sí, reemplazar todo"
                      : "Sí, combinar"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setConfirmMode(null);
                      setPendingFile(null);
                    }}
                    data-testid="backup-cancel-btn"
                    disabled={importing}
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {importResult && (
          <div
            className="mt-3 bg-white border border-emerald-300 rounded-md p-3 text-xs"
            data-testid="backup-import-result"
          >
            <p className="font-bold text-emerald-800 mb-1">
              ✓ Restauración exitosa · modo{" "}
              <code>{importResult.mode}</code>
            </p>
            <p className="text-slate-600 mb-2">
              {importResult.collections_processed} colecciones procesadas
              {importResult.meta?.exported_at && (
                <>
                  {" "}
                  · respaldo del{" "}
                  <b>
                    {new Date(importResult.meta.exported_at).toLocaleString()}
                  </b>
                </>
              )}
            </p>
            <details>
              <summary className="cursor-pointer font-semibold">
                Ver detalle por colección
              </summary>
              <div className="max-h-64 overflow-auto mt-2">
                <table className="w-full text-[11px] font-mono">
                  <thead className="text-left border-b border-slate-200">
                    <tr>
                      <th className="py-1">Colección</th>
                      <th className="py-1 text-right">Entrantes</th>
                      <th className="py-1 text-right">Insertados</th>
                      <th className="py-1 text-right">Actualizados</th>
                      <th className="py-1 text-right">Duplicados</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(importResult.stats || {}).map(
                      ([name, s]) => (
                        <tr key={name} className="border-b border-slate-100">
                          <td className="py-1">{name}</td>
                          <td className="py-1 text-right">{s.incoming}</td>
                          <td className="py-1 text-right">{s.inserted}</td>
                          <td className="py-1 text-right">{s.updated}</td>
                          <td className="py-1 text-right text-slate-400">
                            {s.skipped || 0}
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            </details>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, hint }) {
  return (
    <div className="border border-slate-200 rounded-md px-3 py-2 bg-slate-50">
      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
        {label}
      </p>
      <p className="text-lg font-black text-slate-900 leading-tight mt-0.5">
        {value}
      </p>
      <p className="text-[10px] text-slate-400">{hint}</p>
    </div>
  );
}
